/* Copyright (c) 2020-present CloudIO Inc */

import {
  emptyFunction,
  useStateXAction,
  useStateXAddToUndo,
  useStateXAtomSetter,
  useStateXAtomValue,
  useStateXGetter,
  useStateXRevertUndo,
  useStateXSetter,
} from '@cloudio-saas/statex';
import { useCapturePubSub } from 'common/Context';
import {
  AnyItem,
  getComponentNameMap,
  getManager,
  ItemType,
  Page,
} from 'componentregistry';
import { usePlatform } from 'platform/PlatformHooks';
import { useEffect, useRef } from 'react';
import {
  ConnectableElement,
  DragSourceMonitor,
  useDrag,
  useDrop,
} from 'react-dnd';
import { userAtom } from 'root/useUser';
import {
  clipboardIdAtom,
  designPath,
  dragItemAtom,
  dragTipAtom,
  initialXYCoordAtom,
  pageAtom,
} from './atoms';
import { isDraggable } from './DnDUtils';
import {
  copyItemBetweenPage,
  deleteAction,
  onDragAction,
  onDragEndAction,
  onDragStartAction,
} from './state';
import type { IORef } from './useIORef';

export interface DragItem {
  itemId: string;
  type: 'any';
  itemType: ItemType;
  pageId: string;
  parent: string;
  x: number;
  y: number;
}

function containsChild(page: Page, parent: string, child: string): boolean {
  if (parent === child) {
    return true;
  }
  let id = child;
  let bool = false;
  while (id && id !== 'root') {
    id = page[id]?.parent;
    if (id === parent) {
      bool = true;
      break;
    }
  }
  return bool;
}

const lastTime = { time: 0, x: 0, y: 0 };

export default function useDnD<T extends HTMLElement>(props: {
  appUid: string;
  pageId: string;
  item: AnyItem;
  ref: IORef<T>;
  callback?: () => void;
  endCallback?: () => void;
  disabled?: boolean;
}) {
  const { appUid, pageId, ref, callback, endCallback, disabled, item } = props;
  const startDrag = useStateXAction(onDragStartAction);
  const endDrag = useStateXAction(onDragEndAction);
  const onDrag = useStateXAction(onDragAction);
  const get = useStateXGetter();
  const set = useStateXSetter();
  const setDragTip = useStateXAtomSetter(dragTipAtom);
  const addToUndo = useStateXAddToUndo('io');
  const revertUndo = useStateXRevertUndo('io');
  const platform = usePlatform();
  const deleteItemFn = useStateXAction(deleteAction);

  const dragItemId = useStateXAtomValue(dragItemAtom, {
    shouldComponentUpdate: (value, oldValue) => {
      return value === item.itemId || oldValue === item.itemId;
    },
  });

  const dropRef = useRef({
    ref,
    callback,
    onDrag,
    setDragTip,
  });

  const dragRef = useRef({
    endCallback,
    endDrag,
    startDrag,
    addToUndo,
    callback,
    revertUndo,
  });

  useEffect(() => {
    // console.log('useEffect', ref.current.getBoundingClientRect().x, item.type);
    dropRef.current.callback = callback;
    dropRef.current.ref = ref;

    dragRef.current.callback = callback;
    dragRef.current.endCallback = endCallback;
  }, [callback, endCallback, ref]);

  // useWhyDidYouUpdate('wdyu', { callback, endCallback, item, pageId, ref });
  const pubSub = useCapturePubSub();
  const [, drop] = useDrop(
    () => ({
      accept: 'any',
      hover(sourceItem: DragItem, monitor) {
        const clientOffset = monitor.getClientOffset();
        if (clientOffset) {
          const { time, x, y } = lastTime;
          if (
            x === clientOffset.x &&
            y === clientOffset.y &&
            time + 3000 < Date.now()
          ) {
            pubSub.pub('capture', {
              clientX: clientOffset.x,
              clientY: clientOffset.y,
              label: 'Keep dragging',
            });
            lastTime.time = Date.now();
            lastTime.x = clientOffset.x - 1;
            lastTime.y = clientOffset.y - 1;
          } else if (x !== clientOffset.x || y !== clientOffset.y) {
            lastTime.time = Date.now();
            lastTime.x = clientOffset.x;
            lastTime.y = clientOffset.y;
          }
        }
        // if (!monitor.canDrop()) {
        //   return;
        // }
        const _item = item;

        const el = dropRef.current.ref.current;
        if (
          sourceItem.pageId !== pageId ||
          sourceItem.itemId === _item.itemId ||
          !el ||
          !monitor.isOver({ shallow: true })
        ) {
          return;
        }

        const page = get(pageAtom, {
          params: { appUid, pageId },
        });
        if (!page) {
          throw Error(`Missing page w/ id ${pageId}`);
        }

        const mgr = getManager(_item.type);

        if (containsChild(page, sourceItem.itemId, _item.itemId)) {
          // dragging a parent inside a child component
          return;
        }

        // if dragged on a comp of same type, it can be dropped on to it's parent
        let allowOnDrag = sourceItem.itemType === _item.type;
        if (!allowOnDrag) {
          // if dragged on it's parent, it can be dropped to re-order
          allowOnDrag = _item.itemId === sourceItem.parent;
          if (!allowOnDrag) {
            // if dragged on a sibling comp, it can be dropped to re-order
            allowOnDrag = _item.parent === sourceItem.parent;
          }
        }

        let hoverItem = _item;
        if (!allowOnDrag) {
          let childAllowed = mgr.isChildAllowed(
            page,
            _item,
            sourceItem.itemType,
            {
              appUid,
              pageId,
              itemId: _item.itemId,
              mgr,
              platform,
            },
          );

          if (childAllowed !== true && page[_item.parent]) {
            // check if we can drop on the hover comp's parent
            hoverItem = page[_item.parent];
            const hoverItemParentMgr = getManager(hoverItem.type);
            const allowed = hoverItemParentMgr.isChildAllowed(
              page,
              hoverItem,
              sourceItem.itemType,
              {
                appUid,
                pageId,
                itemId: hoverItem.itemId,
                mgr: hoverItemParentMgr,
                platform,
              },
            );
            if (allowed === true) {
              childAllowed = true;
            } else {
              hoverItem = _item;
            }
          }
          if (typeof childAllowed === 'string') {
            dropRef.current.setDragTip(childAllowed);
            return;
          }
          let isAllowedInsideParent: boolean | string = true;
          if (childAllowed) {
            const _mgr = getManager(sourceItem.itemType);
            isAllowedInsideParent = _mgr.isAllowedInsideParent(
              page,
              hoverItem,
              {
                appUid,
                pageId,
                itemId: hoverItem.itemId,
                mgr: _mgr,
                platform,
              },
            );
            if (typeof isAllowedInsideParent === 'string') {
              dropRef.current.setDragTip(isAllowedInsideParent);
              return;
            }
          }
          if (!(childAllowed && isAllowedInsideParent)) {
            dropRef.current.setDragTip(
              `${getComponentNameMap()[sourceItem.itemType]} inside ${
                getComponentNameMap()[_item.type]
              } is not allowed!`,
            );
            return;
          }
        }

        const offset = monitor.getClientOffset() ?? { x: 0, y: 0 };

        const rect = el.getBoundingClientRect();

        dropRef.current.onDrag({
          appUid,
          itemId: sourceItem.itemId,
          hoverId: _item.itemId,
          pageId,
          offset,
          rect,
          platform,
        });
      },
      drop: (source: DragItem, monitor): DragItem | undefined => {
        const clientOffset = monitor.getClientOffset();
        if (clientOffset) {
          pubSub.pub('capture', {
            clientX: clientOffset.x,
            clientY: clientOffset.y,
            label: 'Drop here',
          });
        }
        if (
          item.itemId === '$clipboard.copy$' ||
          item.itemId === '$clipboard.move$'
        ) {
          set(clipboardIdAtom, '');
          const fromPage = get(pageAtom, {
            params: { appUid, pageId },
          });
          if (!fromPage) return undefined; // this should never happen
          platform.startAsyncTransition(async () => {
            const toPage: Page = {
              root: {
                itemId: 'root',
                type: 'Page',
                children: ['clipboard'],
                props: {},
                parent: 'root',
              },
              clipboard: {
                itemId: 'clipboard',
                type: 'Clipboard',
                children: [],
                props: {},
                parent: 'root',
              },
            };
            const newItemId = copyItemBetweenPage({
              fromPage,
              itemId: source.itemId,
              page: toPage,
              newParentId: 'clipboard',
              retainAllItemIds: true,
              platform,
            });
            toPage.clipboard.children = [newItemId];
            const user = get(userAtom);
            const pageUid = `clipboard-${user?.userName ?? 'guest'}`;
            const r = await platform.sendRequest(
              {
                type: 'PublishEvent',
                event: {
                  appUid: 'cloudio',
                  ctx: 'Clipboard',
                  id: pageUid,
                  ts: new Date().toISOString(),
                  data: toPage,
                },
              },
              { appUid },
            )[0];

            if (r.status === 'success') {
              if (item.itemId === '$clipboard.move$') {
                deleteItemFn({
                  appUid,
                  pageId,
                  itemId: source.itemId,
                  platform,
                });
              } else {
                dragRef.current.revertUndo(
                  designPath({ appUid, pageId }),
                  pageId,
                );
              }
            }
          }, emptyFunction);
          return {
            type: 'any',
            itemType: 'Clipboard',
            parent: 'clipboard',
            pageId,
            itemId: source.itemId,
            x: 0,
            y: 0,
          };
        }
        return undefined;
      },
    }),
    [item, pageId],
  );

  // eslint-disable-next-line @typescript-eslint/ban-types
  const [, drag] = useDrag<DragItem, DragItem, {}>(() => {
    return {
      type: 'any',
      item: (monitor: DragSourceMonitor) => {
        dragRef.current.addToUndo(designPath({ appUid, pageId }), pageId);
        dragRef.current.callback?.();
        dragRef.current.startDrag(item);
        set(
          initialXYCoordAtom,
          monitor.getInitialClientOffset() ?? { x: 0, y: 0 },
        );
        const clientOffset = monitor.getInitialClientOffset();
        if (clientOffset) {
          setTimeout(() => {
            pubSub.pub('capture', {
              clientX: clientOffset.x,
              clientY: clientOffset.y,
              label: 'Drag here',
            });
          }, 600);
        }
        return {
          type: 'any',
          itemType: item.type,
          parent: item.parent,
          pageId,
          itemId: item.itemId,
          x: 0,
          y: 0,
        };
      },
      end: (_item: DragItem | undefined, monitor: DragSourceMonitor) => {
        const dropped = monitor.didDrop();
        let found = false;
        if (_item?.itemType === 'Clipboard') {
          found = true;
        } else if (dropped && _item) {
          const page = get(pageAtom, {
            params: { appUid, pageId },
          });
          if (!page) {
            throw Error(`Missing page w/ id ${pageId}`);
          }
          const nitem = page[_item.itemId];
          found = nitem
            ? !!nitem.parent &&
              page[nitem.parent].children.includes(_item.itemId)
            : false;
        }
        if (!found) {
          console.warn(
            'Item not found in page after drop... reverting!',
            _item?.itemId,
          );
          dragRef.current.revertUndo(designPath({ appUid, pageId }), pageId);
        }
        dragRef.current.endDrag();
        dragRef.current.endCallback?.();
      },
    };
  }, [item, pageId]);

  useEffect(() => {
    const unsubscribe = ref.subscribe((el) => {
      if (!el || disabled) {
        drag(null);
        drop(null);
        return;
      }
      let r: ConnectableElement = el;

      if (isDraggable(item.type)) {
        r = drag(r);
      } else {
        drag(null);
      }

      if (item.parent) {
        drop(r);
      } else {
        drop(null);
      }
    });

    return () => {
      unsubscribe();
      drag(null);
      drop(null);
    };
  }, [disabled, drag, drop, item.parent, item.type, ref]);

  return { dragging: dragItemId === item.itemId };
}
